/* ─────────────────────────────────────────────────────────────
   FACTOR CONFIG — single source of truth for the FA factor set

   The Risk factor was split in two (backend change 2026-07-29):
     • risk_*                    → PRICE risk (beta, volatility, Sharpe, VaR)
     • balance_sheet_quality_*   → NEW factor (interest coverage,
                                   Debt/EBITDA, quick ratio, Altman Z, payout)

   So the UI renders SIX factors wherever factor ratings appear.

   Null-handling contract: every balance_sheet_quality_* field is `null`
   until the model backend starts pushing the new factor. While null the
   chip / row / column is hidden entirely — never rendered as "N/A".
   Use `hasBalanceSheetGrade` / `anyBalanceSheetGrade` to gate rendering.
───────────────────────────────────────────────────────────── */

/* Accepted values for GET /api/v1/stocks/factor-ratings/{ticker}?factor= */
export const FACTOR_PARAMS = Object.freeze([
    "overall",
    "valuation",
    "growth",
    "momentum",
    "financial_profit",
    "risk",
    "balance_sheet_quality",
]);

/* The new factor, described once and reused across every surface. */
export const BALANCE_SHEET_FACTOR = Object.freeze({
    /* ?factor= value */
    param: "balance_sheet_quality",
    /* response / screener field names */
    gradeKey: "balance_sheet_quality_grade",
    scoreKey: "balance_sheet_quality_score",
    /* label for table headers and dense grids */
    label: "Balance Sheet Quality",
    /* uppercase variant to match the screener / factor-grid conventions */
    labelUpper: "BALANCE SHEET QUALITY",
    /* full name for tooltips and the stock-detail row */
    fullLabel: "Balance Sheet Quality",
    tooltip:
        "Measures balance-sheet strength and solvency — interest coverage, " +
        "Debt/EBITDA, quick ratio, Altman Z-Score and dividend payout. A high " +
        "FA grade signals a company that can comfortably service its debt.",
});

/* Pretty labels for the ?factor=balance_sheet_quality breakdown keys.
   Unknown keys fall back to a title-cased version of the key so a new
   sub-metric added by the backend still renders sensibly. */
const BREAKDOWN_LABELS = Object.freeze({
    interest_coverage: "Interest Coverage",
    debt_to_ebitda: "Debt / EBITDA",
    quick_ratio: "Quick Ratio",
    altman_z: "Altman Z-Score",
    payout: "Payout Ratio",
});

/* Preferred display order; anything not listed is appended alphabetically. */
const BREAKDOWN_ORDER = [
    "interest_coverage",
    "debt_to_ebitda",
    "quick_ratio",
    "altman_z",
    "payout",
];

export const breakdownLabel = (key) =>
    BREAKDOWN_LABELS[key] ??
    String(key)
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

/**
 * Normalise the `breakdown` object from
 * GET /api/v1/stocks/factor-ratings/{ticker}?factor=balance_sheet_quality
 * into an ordered array, dropping entries with no value.
 *
 * @param {Record<string, {value: number|null, grade: string|null}>} breakdown
 * @returns {Array<{key: string, label: string, value: number|null, grade: string|null}>}
 */
export const normaliseBalanceSheetBreakdown = (breakdown) => {
    if (!breakdown || typeof breakdown !== "object") return [];

    const keys = Object.keys(breakdown);
    const ordered = [
        ...BREAKDOWN_ORDER.filter((k) => keys.includes(k)),
        ...keys.filter((k) => !BREAKDOWN_ORDER.includes(k)).sort(),
    ];

    return ordered
        .map((key) => {
            const entry = breakdown[key] || {};
            return {
                key,
                label: breakdownLabel(key),
                value: entry.value ?? null,
                grade: entry.grade ?? null,
            };
        })
        .filter((row) => row.value != null || row.grade != null);
};

/* ── Null-handling helpers ── */

/** True when a single row/object carries a usable Balance-Sheet Quality grade. */
export const hasBalanceSheetGrade = (row) => {
    const g = row?.[BALANCE_SHEET_FACTOR.gradeKey];
    return g != null && g !== "" && g !== "-";
};

/** True when at least one row in a list carries the new grade. */
export const anyBalanceSheetGrade = (rows) =>
    Array.isArray(rows) && rows.some(hasBalanceSheetGrade);
